import React, { FC, useEffect } from 'react';
import { ArrowLeftSmall } from '@wix/wix-ui-icons-common';
import cs from 'classnames';
import { VIEW_MODE } from '../../utils/helpers';
import s from './ToolBar.scss';
import { WixLogo } from '../partials/WixLogo/WixLogo';
import { useTranslation } from '@wix/wix-i18n-config';
import { useTemplateContext } from '../../context/TemplateContext/TemplateContext';
import { InfoButton } from '../InfoButton/InfoButton';
import { useUIContext } from '../../context/UIContext/UIContext';
import { reportEditorClick } from '../../utils/bi';
import { toolBarDataHooks } from './ToolBar.dataHooks';
import DesktopIcon from './desktop-new-ui.svg';
import MobileIcon from './mobile-new-ui.svg';

export const ToolBar: FC<{ dataHook?: string }> = ({ dataHook }) => {
  const { t } = useTranslation();
  const { viewMode, toggleViewMode } = useUIContext();
  const { template } = useTemplateContext();
  const isShowBackButton = template.backToTemplatesUrl || template.origin !== undefined;

  useEffect(() => {
    window.templateEditUrl = template.editorUrl; // Adoric need to have this link in the window
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEditorClick = () => {
    reportEditorClick({ origin: 'viewer_top' });
  };

  const onBackToTemplatesClick = () => {
    if (template.origin === 'intro') {
      window.history.back();
    } else if (template.backToTemplatesUrl !== undefined) {
      window.location.assign(template.backToTemplatesUrl);
    }
  };

  const isDashboardFirst = template.editorUrl.includes('dashboardFirst=true');

  return (
    <div data-hook={dataHook} className={s.toolBar}>
      <div className={s.row}>
        <div className={s.logo}>
          <WixLogo dataHook={toolBarDataHooks.logo} />
        </div>
        {isShowBackButton && (
          <>
            <hr className={s.backButtonDivider} />
            <div
              onClick={onBackToTemplatesClick}
              className={s.backToTemplatesButton}
              data-hook={toolBarDataHooks.backToTemplatesButton}
            >
              <ArrowLeftSmall className={s.backButtonIcon} />
              <div className={s.label}>{t('template.viewer.header.backToTemplates')}</div>
            </div>
          </>
        )}
        <div className={s.deviceToggle}>
          <button
            className={cs(s.newIcon, {
              [s.newUiSelected]: viewMode === VIEW_MODE.DESKTOP,
            })}
            onClick={() => toggleViewMode(VIEW_MODE.DESKTOP)}
            data-hook={toolBarDataHooks.desktopView}
          >
            <DesktopIcon className={s.newDesktopIcon} />
            <span className={s.hide}>{t('a11y.desktop.button')}</span>
          </button>
          <hr className={s.newHr} />
          <button
            className={cs(s.newIcon, { [s.newUiSelected]: viewMode === VIEW_MODE.MOBILE })}
            onClick={() => toggleViewMode(VIEW_MODE.MOBILE)}
            data-hook={toolBarDataHooks.mobileView}
          >
            <MobileIcon className={s.newMobileIcon} />
            <span className={s.hide}>{t('a11y.mobile.button')}</span>
          </button>
        </div>
      </div>
      <div className={s.actionMenu}>
        <div className={s.elementsGroup}>
          <p data-hook={toolBarDataHooks.toolBarTitle} className={isShowBackButton ? s.responsiveTitle : s.title}>
            {isDashboardFirst ? t('template.viewer.title.chooseTemplate') : t('template.viewer.title')}
          </p>
          <InfoButton dataHook={toolBarDataHooks.infoView} />
          {/* eslint-disable-next-line react/jsx-no-target-blank */}
          <a
            className={cs(s.button, s.newBtnUi)}
            data-hook={toolBarDataHooks.editorLink}
            href={template.editorUrl}
            target="_blank"
            tabIndex={0}
            onClick={() => onEditorClick()}
          >
            {isDashboardFirst ? t('template.viewer.choose.button') : t('template.viewer.edit.button')}
          </a>
        </div>
      </div>
    </div>
  );
};
